import axios from "axios";
import { store } from "../Store/store";
import { authActions } from "../Features/authSlice";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor: attach refresh token from localStorage as Authorization header.
// The backend's verifyAccessToken middleware uses this as a fallback when the
// access token cookie has expired, enabling silent token refresh.
api.interceptors.request.use(
  (config) => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      config.headers.Authorization = refreshToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, attempt a single token refresh. If it succeeds,
// retry the original request. If it fails (refresh token also expired), force
// logout and redirect to /login.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request until the ongoing refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = token;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const storedRefreshToken = localStorage.getItem("refreshToken");
        const rawToken = storedRefreshToken?.replace("bearer ", "");

        if (!rawToken) {
          throw new Error("No refresh token available");
        }

        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
          { refreshToken: rawToken },
          { withCredentials: true }
        );

        const newRefreshToken = `bearer ${data.refreshToken}`;
        localStorage.setItem("refreshToken", newRefreshToken);

        processQueue(null, newRefreshToken);
        originalRequest.headers.Authorization = newRefreshToken;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Force logout
        localStorage.removeItem("refreshToken");
        store.dispatch(authActions.logout());
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
